import React, { useState, useEffect, useCallback } from "react";
import Alert from "@material-ui/lab/Alert";
import { useParams } from "react-router-dom";
import {
	Typography,
	Grid,
	Button,
	TextField,
	Hidden,
	Card,
	CardContent,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

const useStyles = makeStyles((theme) => ({
	form: {
		width: "100%", // Fix IE 11 issue.
	},
	submit: {
		margin: theme.spacing(2, 0, 1),
	},
	chips: {
		display: "flex",
		flexWrap: "wrap",
	},
	chip: {
		margin: 2,
	},
}));

export default function FilesEdit(props) {
	const classes = useStyles();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [file, setFile] = useState();
	const [updatedFile, setUpdatedFile] = useState();
	const { enqueueSnackbar } = useSnackbar();

	const fetchFile = useCallback(
		async (id) => {
			try {
				let res = await fetch(
					`${process.env.REACT_APP_API_URL}/files?_id=${id}`,
					{
						credentials: "include",
					}
				);
				res = await res.json();
				if (res.success) {
					res.data = {
						...res.data[0],
						uploadedAt: dayjs(res.data[0].uploadedAt).format(
							"MM-DD-YY h:mm:ss A"
						),
						lastModifiedAt: dayjs(res.data[0].lastModifiedAt).format(
							"MM-DD-YY h:mm:ss A"
						),
					};
					setFile(res.data);
				} else {
					enqueueSnackbar(res.error, { variant: "error" });
				}
			} catch (e) {
				console.log(e);
			}
		},
		[enqueueSnackbar]
	);

	useEffect(() => {
		(async () => {
			await fetchFile(id);
			setLoading(false);
		})();
	}, [fetchFile, id]);

	// When file is set, set the updated file info to the file
	useEffect(() => {
		setUpdatedFile(file);
	}, [file]);

	const onChange = (e) => {
		setUpdatedFile({ ...updatedFile, [e.target.name]: e.target.value });
	};

	const onSubmit = async (e) => {
		e.preventDefault();

		//Remove any keys with an empty str as the value
		for (const key of Object.keys(updatedFile)) {
			if (updatedFile[key] === "") {
				delete updatedFile[key];
			}
		}

		// If updated file ext doesn't match, reset the name and output error message
		if (
			file.fileName.split(".").pop() !== updatedFile.fileName.split(".").pop()
		) {
			setUpdatedFile({ ...updatedFile, fileName: file.fileName });
			return enqueueSnackbar(
				`Please do not change or remove the original extension of '.${file.fileName
					.split(".")
					.pop()}'`,
				{ variant: "error" }
			);
		}

		if (!updatedFile.fileName) {
			return enqueueSnackbar("File name is required", { variant: "error" });
		}

		await fetch(`${process.env.REACT_APP_API_URL}/files/${id}/info`, {
			method: "PUT",
			credentials: "include",
			body: JSON.stringify(updatedFile),
			headers: { "Content-Type": "application/json" },
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.success) {
					enqueueSnackbar("File updated successfully!", {
						variant: "success",
					});
				} else {
					console.log(data);
					const errorArray = data.error.split(",");
					errorArray.forEach((error) => {
						enqueueSnackbar(`${error}`, { variant: "error" });
					});
				}
			});

		fetchFile(id);
	};

	if (loading) {
		return <div>Loading...</div>;
	}

	return (
		<>
			<Typography component="h1" variant="h4">
				Edit File
			</Typography>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={12} md={9}>
					{file.thumbnailName && (
						<Card
							style={{
								margin: "1rem 0",
								padding: "1rem",
								backgroundColor: "black",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								borderRadius: "5px",
							}}
						>
							<img
								alt={file.thumbnailName}
								style={{
									width: "auto",
									maxWidth: "100%",
									maxHeight: "50vh",
								}}
								src={`${process.env.REACT_APP_FILE_URL}/${file.fileName}`}
							/>
						</Card>
					)}
					<Alert severity="info">
						The file name will automatically have a timestamp prepended after
						updating to prevent duplicate file names.
					</Alert>
					<TextField
						variant="outlined"
						margin="normal"
						fullWidth
						label="File Name"
						name="fileName"
						size="small"
						onChange={onChange}
						value={(updatedFile && updatedFile.fileName) ?? ""}
					/>
					<Button
						type="submit"
						onClick={onSubmit}
						fullWidth
						variant="contained"
						color="primary"
						className={classes.submit}
					>
						Save
					</Button>
				</Grid>
				<Grid item xs={12} sm={12} md={3}>
					<Card style={{ marginTop: "1rem" }}>
						<CardContent>
							<Hidden smDown>
								<Button
									type="submit"
									onClick={onSubmit}
									fullWidth
									variant="contained"
									color="primary"
									className={classes.submit}
								>
									Save
								</Button>
							</Hidden>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								label="Uploaded By"
								size="small"
								value={(file && file.uploadedBy.email) ?? ""}
								disabled
							/>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								label="Uploaded At"
								size="small"
								value={(file && file.uploadedAt) ?? ""}
								disabled
							/>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								label="Last Modified By"
								size="small"
								value={(file && file.lastModifiedBy.email) ?? ""}
								disabled
							/>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								label="Last Modified At"
								size="small"
								value={(file && file.lastModifiedAt) ?? ""}
								disabled
							/>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</>
	);
}
